/**
 * 공지사항 작성/수정 페이지 (V83)
 *
 * 공지사항 생성 및 수정 폼
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Upload,
  X,
  FileText,
  Image,
  FileSpreadsheet,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react';
import {
  useAnnouncementDetail,
  useAnnouncementMutations,
  useAnnouncementUpload,
  useAnnouncementPermission,
  AnnouncementAttachment,
  AnnouncementBoardType,
  ANNOUNCEMENT_BOARD_LABELS,
  DEFAULT_ANNOUNCEMENT_BOARD_TYPE,
  // V66: 노출기간 옵션
  AnnouncementVisibilityDurationDays,
  VISIBILITY_DURATION_OPTIONS,
} from '../../shared/hooks/useAnnouncements';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import RichTextEditor from '../../shared/components/RichTextEditor';
import { sanitizeAnnouncementHtml } from '../../shared/components/RichContentViewer';
import {
  PopupSettingsSection,
  PopupFormState,
  INITIAL_POPUP_FORM_STATE,
  buildPopupPayload,
  isoToKstDate,
  validatePopupForm,
} from './PopupSettingsSection';

export function AnnouncementFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // 권한 확인
  const { canWrite, isLoading: permissionLoading } = useAnnouncementPermission();
  const { roleAssignments } = useAuthContext();
  const isSystemAdmin = roleAssignments.some((r) => r.roleId === 'system_admin');

  // 기존 데이터 조회 (수정 모드)
  const { announcement, isLoading: detailLoading } = useAnnouncementDetail(
    isEditMode ? id : null
  );

  // Mutations
  const { createAnnouncement, updateAnnouncement, isSubmitting, error: mutationError } =
    useAnnouncementMutations();

  // 파일 업로드
  const { uploadFile, uploadFiles, uploadProgress, isUploading, clearProgress } = useAnnouncementUpload();

  // V279: 리치 에디터 인라인 이미지 업로드 핸들러 — 첨부파일 목록과 분리
  // V279.2: trackProgress=false 로 호출하여 첨부파일 진행 UI를 더럽히지 않음
  const handleEditorImageUpload = async (file: File): Promise<string> => {
    const att = await uploadFile(file, { trackProgress: false });
    return att.url;
  };

  // 폼 상태
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [companyCode, setCompanyCode] = useState<'KR' | 'CN' | 'ALL' | undefined>(undefined); // V60: 국가 스코프
  // V64: 게시판 분류 (필수)
  const [boardType, setBoardType] = useState<AnnouncementBoardType>(DEFAULT_ANNOUNCEMENT_BOARD_TYPE);
  const [attachments, setAttachments] = useState<AnnouncementAttachment[]>([]);
  const [notifyOnPublish, setNotifyOnPublish] = useState(false); // V63 Batch6: 알림 발송 여부
  // V66: 노출기간 선택 (null = 제한 없음)
  const [visibilityDurationDays, setVisibilityDurationDays] =
    useState<AnnouncementVisibilityDurationDays | null>(null);
  // V66: 수정 화면에서 visibilityDurationDays가 실제로 변경됐는지 추적
  // (변경 안 했으면 update payload에서 미포함하여 BE가 기존 autoHideAt 보존)
  const [visibilityDurationDirty, setVisibilityDurationDirty] = useState(false);
  // V70G7B: 팝업 설정
  const [popupForm, setPopupForm] = useState<PopupFormState>(INITIAL_POPUP_FORM_STATE);
  const [wasPopup, setWasPopup] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 수정 모드: 기존 데이터로 폼 초기화
  // V279: legacy plain 본문은 HTML로 1회 변환(줄바꿈 → <br/>) 후 에디터에 로드
  useEffect(() => {
    if (isEditMode && announcement) {
      setTitle(announcement.title);
      if (announcement.contentFormat === 'html') {
        setContent(announcement.content || '');
      } else {
        const escaped = (announcement.content || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br/>');
        setContent(escaped ? `<p>${escaped}</p>` : '');
      }
      setIsPinned(announcement.isPinned);
      setCompanyCode(announcement.companyCode); // V60
      setBoardType(announcement.boardType || DEFAULT_ANNOUNCEMENT_BOARD_TYPE); // V64
      setAttachments(announcement.attachments || []);
      // V66: 기존 노출기간 표시 (없거나 0이면 제한없음)
      const existingDuration = announcement.visibilityDurationDays;
      setVisibilityDurationDays(
        existingDuration === 1 || existingDuration === 7 || existingDuration === 14 || existingDuration === 30
          ? existingDuration
          : null
      );
      setVisibilityDurationDirty(false);
      // V70G7B
      setWasPopup(announcement.isPopup === true);
      setPopupForm({
        isPopup: announcement.isPopup === true,
        startDate: isoToKstDate(announcement.popupStartAt),
        endDate: isoToKstDate(announcement.popupEndAt),
        dismissPolicy: announcement.popupDismissPolicy || INITIAL_POPUP_FORM_STATE.dismissPolicy,
        priority: String(announcement.popupPriority ?? INITIAL_POPUP_FORM_STATE.priority),
        bumpRevision: false,
      });
    }
  }, [isEditMode, announcement]);

  // 권한 없음
  if (!permissionLoading && !canWrite) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '60px', textAlign: 'center', color: '#dc2626' }}>
          공지사항 작성 권한이 없습니다.
        </div>
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigate('/announcements')}
            style={{
              padding: '8px 16px',
              background: 'var(--color-gray-100)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            목록으로
          </button>
        </div>
      </div>
    );
  }

  // 로딩
  if (permissionLoading || (isEditMode && detailLoading)) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '60px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
          불러오는 중...
        </div>
      </div>
    );
  }

  // 파일 크기 포맷
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 파일 아이콘
  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return <Image size={16} />;
    if (contentType.includes('spreadsheet') || contentType.includes('excel'))
      return <FileSpreadsheet size={16} />;
    return <FileText size={16} />;
  };

  // 파일 선택
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setError(null);
    clearProgress();

    try {
      const newAttachments = await uploadFiles(Array.from(files));
      setAttachments((prev) => [...prev, ...newAttachments]);
    } catch (err: any) {
      setError(err.message || '파일 업로드 중 오류가 발생했습니다.');
    }

    // 입력 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 첨부파일 삭제 (로컬에서만, 실제 Storage 삭제는 저장 시)
  const removeAttachment = (attachmentId: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  };

  // 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 유효성 검사
    if (!title.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }
    // V279: 리치 에디터는 빈 본문이어도 <p></p>를 남기므로 textContent로 검증
    const probe = typeof window !== 'undefined' ? document.createElement('div') : null;
    if (probe) probe.innerHTML = content;
    const plainProbe = (probe?.textContent || '').trim();
    const hasMedia = /<img[\s>]|<table[\s>]/i.test(content);
    if (!plainProbe && !hasMedia) {
      setError('내용을 입력해주세요.');
      return;
    }

    const popupError = validatePopupForm(popupForm);
    if (popupError) {
      setError(popupError);
      return;
    }
    const popupPayload = buildPopupPayload(popupForm, wasPopup, isEditMode);

    // V279: FE 1차 sanitize (저장 시점)
    const sanitizedContent = sanitizeAnnouncementHtml(content);
    try {
      if (isEditMode) {
        // 수정
        // V66: visibilityDurationDays 가 dirty(사용자가 라디오를 한번이라도 변경) 일 때만 payload에 포함
        await updateAnnouncement({
          id: id!,
          title: title.trim(),
          content: sanitizedContent,
          contentFormat: 'html',
          isPinned,
          boardType, // V64
          visibilityRoleIds: ['all_employees'],
          ...(visibilityDurationDirty ? { visibilityDurationDays } : {}), // V66
          ...popupPayload, // V70G7B
          attachments,
        });
        navigate(`/announcements/${id}`);
      } else {
        // 생성
        const newId = await createAnnouncement({
          title: title.trim(),
          content: sanitizedContent,
          contentFormat: 'html',
          isPinned,
          boardType, // V64
          visibilityRoleIds: ['all_employees'],
          ...(isSystemAdmin && companyCode ? { companyCode } : {}), // V60: system_admin만 스코프 선택
          visibilityDurationDays, // V66: null = 제한 없음
          notifyOnPublish, // V63 Batch6: 알림 발송 여부
          ...popupPayload, // V70G7B
          attachments: attachments.map((a) => ({
            id: a.id,
            name: a.name,
            url: a.url,
            size: a.size,
            contentType: a.contentType,
            storagePath: a.storagePath,
          })),
        });
        navigate(`/announcements/${newId}`);
      }
    } catch (err: any) {
      setError(err.message || '저장 중 오류가 발생했습니다.');
    }
  };

  // 취소
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/announcements/${id}`);
    } else {
      navigate('/announcements');
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* 뒤로가기 */}
      <button
        onClick={handleCancel}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-gray-600)',
          fontSize: '14px',
          marginBottom: '16px',
        }}
      >
        <ArrowLeft size={18} />
        {isEditMode ? '상세로 돌아가기' : '목록으로'}
      </button>

      {/* 폼 카드 */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <h1
            style={{
              fontSize: '20px',
              fontWeight: 600,
              margin: 0,
              color: 'var(--color-gray-900)',
            }}
          >
            {isEditMode ? '공지사항 수정' : '새 공지사항'}
          </h1>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px' }}>
            {/* 에러 메시지 */}
            {(error || mutationError) && (
              <div
                style={{
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '14px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <AlertCircle size={18} />
                {error || mutationError}
              </div>
            )}

            {/* 제목 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--color-gray-700)',
                  marginBottom: '6px',
                }}
              >
                제목 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="공지사항 제목을 입력하세요"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* 내용 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--color-gray-700)',
                  marginBottom: '6px',
                }}
              >
                내용 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={setContent}
                onImageUpload={handleEditorImageUpload}
                placeholder="공지사항 내용을 입력하세요"
              />
            </div>

            {/* V64: 게시판 분류 (필수) */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--color-gray-700)',
                  marginBottom: '8px',
                }}
              >
                게시판 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {(Object.keys(ANNOUNCEMENT_BOARD_LABELS) as AnnouncementBoardType[]).map((bt) => {
                  const active = bt === boardType;
                  return (
                    <button
                      key={bt}
                      type="button"
                      onClick={() => setBoardType(bt)}
                      style={{
                        padding: '8px 14px',
                        background: active ? 'var(--color-primary)' : 'white',
                        color: active ? 'white' : 'var(--color-gray-700)',
                        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-gray-300)'}`,
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: active ? 500 : 400,
                        transition: 'all 0.2s',
                      }}
                    >
                      {ANNOUNCEMENT_BOARD_LABELS[bt]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 상단 고정 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={(e) => setIsPinned(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontSize: '14px', color: 'var(--color-gray-700)' }}>
                  상단 고정 (중요 공지)
                </span>
              </label>
            </div>

            {/* V66: 노출기간 선택 (기본 제한 없음). 만료 시 다음날 08:00 KST 자동 미노출 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--color-gray-700)',
                  marginBottom: '8px',
                }}
              >
                노출기간
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {VISIBILITY_DURATION_OPTIONS.map((opt) => {
                  const active = visibilityDurationDays === opt.value;
                  return (
                    <button
                      key={String(opt.value ?? 'none')}
                      type="button"
                      onClick={() => {
                        setVisibilityDurationDays(opt.value);
                        setVisibilityDurationDirty(true);
                      }}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '8px',
                        border: active ? '1px solid var(--color-blue-500)' : '1px solid var(--color-gray-300)',
                        background: active ? 'var(--color-blue-50)' : 'white',
                        color: active ? 'var(--color-blue-700)' : 'var(--color-gray-700)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: active ? 500 : 400,
                        transition: 'all 0.2s',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                기간을 선택하면 해당 일수 이후의 다음 날 오전 8시(KST)에 자동으로 미노출 처리됩니다. 제한 없음을 선택하면 계속 노출됩니다.
              </div>
            </div>

            {/* V70G7B: 팝업 설정 */}
            <PopupSettingsSection
              value={popupForm}
              onChange={setPopupForm}
              isEditMode={isEditMode}
              wasPopup={wasPopup}
            />

            {/* V63 Batch6: 알림 발송 여부 (신규 생성 시에만) */}
            {!isEditMode && (
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={notifyOnPublish}
                    onChange={(e) => setNotifyOnPublish(e.target.checked)}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px', color: 'var(--color-gray-700)' }}>
                    이메일 알림 발송
                  </span>
                </label>
                <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                  체크 시 대상 직원에게 공지사항 등록 알림 이메일이 발송됩니다.
                </div>
              </div>
            )}

            {/* V60: 공지 대상 국가 (system_admin만 선택 가능) */}
            {isSystemAdmin && !isEditMode && (
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-gray-700)',
                    marginBottom: '8px',
                  }}
                >
                  공지 대상 국가
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { value: 'ALL' as const, label: '전체 (한국+대련)' },
                    { value: 'KR' as const, label: '한국' },
                    { value: 'CN' as const, label: '대련' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setCompanyCode(opt.value)}
                      style={{
                        padding: '8px 14px',
                        background: (companyCode || 'ALL') === opt.value ? 'var(--color-primary)' : 'white',
                        color: (companyCode || 'ALL') === opt.value ? 'white' : 'var(--color-gray-700)',
                        border: `1px solid ${(companyCode || 'ALL') === opt.value ? 'var(--color-primary)' : 'var(--color-gray-300)'}`,
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: (companyCode || 'ALL') === opt.value ? 500 : 400,
                        transition: 'all 0.2s',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                  일반 관리자는 자동으로 자신의 국가 공지로 생성됩니다.
                </div>
              </div>
            )}

            {/* 첨부파일 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--color-gray-700)',
                  marginBottom: '8px',
                }}
              >
                첨부파일
              </label>

              {/* 파일 선택 버튼 */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.zip"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                style={{
                  padding: '10px 16px',
                  background: 'var(--color-gray-100)',
                  border: '1px dashed var(--color-gray-300)',
                  borderRadius: '8px',
                  cursor: isUploading ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  color: 'var(--color-gray-600)',
                }}
              >
                <Upload size={16} />
                파일 선택 (PDF, Office, 이미지, 텍스트, ZIP)
              </button>

              {/* 업로드 진행 상태 */}
              {uploadProgress.length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {uploadProgress.map((p) => (
                    <div
                      key={p.fileId}
                      style={{
                        padding: '8px 12px',
                        background: 'var(--color-gray-50)',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      {p.status === 'uploading' || p.status === 'confirming' ? (
                        <Loader2 size={16} className="animate-spin" style={{ color: 'var(--color-primary)' }} />
                      ) : p.status === 'done' ? (
                        <Check size={16} style={{ color: '#10b981' }} />
                      ) : p.status === 'error' ? (
                        <AlertCircle size={16} style={{ color: '#dc2626' }} />
                      ) : null}
                      <span style={{ flex: 1, fontSize: '13px', color: 'var(--color-gray-700)' }}>
                        {p.fileName}
                      </span>
                      {p.status === 'uploading' && (
                        <span style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                          {p.progress}%
                        </span>
                      )}
                      {p.status === 'error' && (
                        <span style={{ fontSize: '12px', color: '#dc2626' }}>{p.error}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* 첨부된 파일 목록 */}
              {attachments.length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {attachments.map((att) => (
                    <div
                      key={att.id}
                      style={{
                        padding: '10px 12px',
                        background: 'white',
                        border: '1px solid var(--color-gray-200)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: 'var(--color-gray-500)' }}>
                          {getFileIcon(att.contentType)}
                        </span>
                        <span style={{ fontSize: '13px', color: 'var(--color-gray-700)' }}>
                          {att.name}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--color-gray-400)' }}>
                          ({formatFileSize(att.size)})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(att.id)}
                        style={{
                          padding: '4px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-gray-400)',
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 버튼 영역 */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--color-gray-200)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
            }}
          >
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: '10px 20px',
                background: 'white',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isUploading}
              style={{
                padding: '10px 20px',
                background: isSubmitting || isUploading ? 'var(--color-gray-300)' : 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isSubmitting || isUploading ? 'default' : 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {isEditMode ? '수정' : '등록'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AnnouncementFormPage;
